/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import produce from 'immer';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { filtersInitialState } from 'app/views/layout/views/search/components/filters/utils/definitions';
import { enrichStaticFilters } from 'app/views/layout/views/search/utils/filters/enrichStaticFilters';
import { appSearchParamsToURLSearchParams, appSearchStringToParams, } from 'utils/url/appSearchParams';
import { createReadSearchResultsSearchParams } from './transformArg';
/**
 * Transforms the endpoint responses of search endpoints.
 *
 * @param response The raw response.
 */
function transformSearchResultsResponse(response) {
    transformSearchResultsAttributes(response);
    return {
        data: {
            // `v2/search` endpoint can return multiple scopes at a time. However
            // because we define the arg for that endpoint that you can only request
            // one scope at a time, the raw response will always have a single entry
            // in the result. Hence why we can safely take the first entry, which
            // would correspond with the scope defined by the arg.
            results: response.data[0],
        },
        meta: response.meta,
    };
}
/**
 * Transforms the read search results endpoint response.
 *
 * @param response The response from the endpoint.
 * @param arg The arg used to call the endpoint.
 */
export function transformReadSearchResultsResponse(response, arg) {
    const { hits_type, nb_pages, page, hits_per_page: pageSize, query, } = response.data[0];
    const searchParams = typeof arg === 'string'
        ? appSearchStringToParams(arg.split('?')[1])
        : createReadSearchResultsSearchParams(hits_type, arg);
    return Object.assign(Object.assign({}, transformSearchResultsResponse(response)), { meta: {
            next_page: page < nb_pages - 1
                ? `/v2/search?${appSearchParamsToURLSearchParams(Object.assign(Object.assign({}, searchParams), { query,
                    pageSize, page: page + 1 }))}`
                : null,
            prev_page: page > 0
                ? `/v2/search?${appSearchParamsToURLSearchParams(Object.assign(Object.assign({}, searchParams), { query,
                    pageSize, page: page - 1 }))}`
                : null,
        } });
}
/**
 * Transforms the read advanced search results endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadAdvancedSearchResultsResponse(response) {
    transformSearchResultsAttributes(response);
    return {
        data: {
            results: response.data.map((result) => (Object.assign(Object.assign({}, result), { nb_pages: result.nb_pages, 
                // Pagination is 1-based in the frontend and 0-based in the backend
                page: result.page + 1 }))),
        },
        meta: response.meta,
    };
}
/**
 * Transforms the read saved search list endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadSavedSearchListResponse(response) {
    var _a;
    const data = (_a = response.data.saved_searches) !== null && _a !== void 0 ? _a : response.data;
    return {
        data: {
            saved_searches: data.map(transformSavedSearch),
        },
        meta: response.meta,
    };
}
/**
 * Transforms the create saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformCreateSavedSearchResponse(response) {
    var _a;
    return {
        data: {
            saved_search: transformSavedSearch((_a = response.data.saved_search) !== null && _a !== void 0 ? _a : response.data),
        },
        meta: response.meta,
    };
}
/**
 * Transforms the read saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadSavedSearchResponse(response) {
    var _a;
    return {
        data: {
            saved_search: transformSavedSearch((_a = response.data.saved_search) !== null && _a !== void 0 ? _a : response.data),
        },
        meta: response.meta,
    };
}
/**
 * Transforms the update saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformUpdateSavedSearchResponse(response) {
    var _a;
    return {
        data: {
            saved_search: transformSavedSearch((_a = response.data.saved_search) !== null && _a !== void 0 ? _a : response.data),
        },
        meta: response.meta,
    };
}
/**
 * Transforms the delete saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformDeleteSavedSearchResponse(response) {
    return {
        data: {
            id: response.data,
        },
        meta: response.meta,
    };
}
export function transformReadSearchEnrichedFiltersResponse(response) {
    return {
        data: {
            filters: response.data.params.filters,
        },
        meta: response.meta,
    };
}
function transformSavedSearch(savedSearch) {
    return produce(savedSearch, (draft) => {
        var _a;
        draft.params.query = (_a = draft.params.query) !== null && _a !== void 0 ? _a : '';
        draft.params.filters = (draft.params.filters
            ? enrichStaticFilters(draft.params.filters)
            : filtersInitialState);
    });
}
/**
 * Transforms the search results attributes from a raw response.
 *
 * @param response The raw response.
 */
function transformSearchResultsAttributes(response) {
    response.data.forEach((hitsType) => hitsType.hits.forEach((hit) => {
        if (typeof hit.initial_channel !== 'undefined') {
            hit.initial_channel = convertChannelCasing(hit.initial_channel, 'snake');
        }
        if (typeof hit.inboundmessagecount === 'string') {
            hit.inboundmessagecount = JSON.parse(hit.inboundmessagecount);
        }
        if (typeof hit.outboundmessagecount === 'string') {
            hit.outboundmessagecount = JSON.parse(hit.outboundmessagecount);
        }
        delete hit.document_type;
        delete hit.join_field;
    }));
}
